/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import { Row, Col } from "react-awesome-styled-grid";
import { Link, useParams } from "react-router-dom";
import { Table, Spacer, Tag, Tooltip, Text, SVGIcon } from "@nordcloud/gnui";
import { TagWrap } from "~/components/Applications/ApplicationItem";
import { CollapseItem } from "~/components/CollapseItem/CollapseItem";
import { Popover } from "~/components/Popover/Popover";
import { PopoverButton } from "~/components/Popover/PopoverButton";
import { EditSLA } from "~/components/SLA/EditSLA";
import { DetailsTitle, StyledLinkSmall } from "~/components/Styled/Common";
import FlexContainer from "~/components/Styled/FlexContainer";
import { ManageServiceTools } from "~/components/Tooling/ManageServiceTools";
import { dateFormatWithHours } from "~/constants";
import { RouteParams, Tool } from "~/models";
import { ROUTES } from "~/routing";
import {
  displayServiceHours,
  displaySlaType,
  displaySlaUnitType,
  displayToolColor,
  formatServiceName,
} from "~/tools";
import { Badge } from "../Badge/Badge";
import { ServiceDeactivate } from "./ServiceDeactivate";
import { AssignedTool, ServiceAttachment, AttachedSlas } from "./types";
import { UnassignService } from "./UnassignService";

type ServiceItemProps = {
  serviceAttachment: ServiceAttachment;
  refetch: () => void;
  envName: string;
  canEdit?: boolean;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function ServiceItem({
  serviceAttachment: {
    service,
    attachedSlas,
    attachedTools,
    assignmentRule,
    log,
  },
  refetch,
  envName,
  canEdit = true,
}: ServiceItemProps) {
  const state = useParams<RouteParams>();
  const { customerId } = state;
  const [isEmpty, setIsEmpty] = React.useState(true);
  const [isPopover, setIsPopover] = React.useState(false);
  type ItemType = {
    tool: Tool;
  };
  const flatAttachedTools = attachedTools?.map((item: ItemType) => item.tool);
  const isDeactivated = service?.deactivated;

  React.useEffect(() => {
    flatAttachedTools.length === 0 && attachedSlas?.length === 0
      ? setIsEmpty(true)
      : setIsEmpty(false);
  }, [attachedSlas, flatAttachedTools, setIsEmpty, envName, refetch]);

  React.useEffect(() => {
    service?.deactivated ? setIsEmpty(true) : setIsEmpty(false);
  }, [service?.deactivated]);

  let author = log.modified.author;
  if (log.created.time !== log.modified.time) {
    author = log.created.author;
  }

  return (
    <CollapseItem
      buttons={
        <>
          <Tooltip
            status="notification"
            caption="Service Group"
            hideTimeout={0}
          >
            <Tag
              color="notification"
              css={{ margin: "0 1rem 0 auto" }}
              text={service?.serviceGroup?.name}
            />
          </Tooltip>

          <FlexContainer>
            <Tooltip
              status="notification"
              position="right"
              caption={
                <FlexContainer aria-label="notification" direction="column">
                  <Text m="0" mr="0.3rem" size="sm">
                    Applied by:
                  </Text>
                  {assignmentRule ? (
                    <StyledLinkSmall
                      to={`${ROUTES.customers.index}/${customerId}/assignment-rules/${assignmentRule?.id}`}
                    >
                      {assignmentRule?.name}
                    </StyledLinkSmall>
                  ) : (
                    <Text size="sm" m="0" weight="medium">
                      {author}
                    </Text>
                  )}
                </FlexContainer>
              }
            >
              <FlexContainer css={{ marginRight: "1rem" }}>
                <SVGIcon size="sm" name="info" aria-label="info" />
              </FlexContainer>
            </Tooltip>
          </FlexContainer>
          {canEdit && (
            <Popover
              alignRight
              setIsPopover={setIsPopover}
              isPopover={isPopover}
              trigger={<PopoverButton />}
            >
              {!isDeactivated && (
                <>
                  <ManageServiceTools
                    serviceId={service.id}
                    attachedTools={flatAttachedTools}
                    refetch={refetch}
                    setIsPopover={setIsPopover}
                  />
                  <UnassignService
                    envName={envName}
                    service={service}
                    refetch={refetch}
                    setIsPopover={setIsPopover}
                  />
                </>
              )}
              <ServiceDeactivate
                isDeactivated={isDeactivated}
                service={service}
                refetch={refetch}
                setIsPopover={setIsPopover}
              />
            </Popover>
          )}
        </>
      }
      name={
        <FlexContainer>
          <Link to={`${ROUTES.serviceCommitments.index}/${service.id}`}>
            {formatServiceName(service)}
          </Link>
          <Text tag="div" color="warning" ml="0.5rem">
            {isDeactivated && "(Deactivated)"}
          </Text>
        </FlexContainer>
      }
    >
      {!isEmpty && (
        <div>
          <FlexContainer css={{ margin: "1rem 0 0 0.7rem" }}>
            {attachedTools?.length !== 0 && (
              <DetailsTitle css={{ minWidth: "3rem", marginBottom: "auto" }}>
                Tools:
              </DetailsTitle>
            )}
            <FlexContainer wrap="wrap" justify="flex-start">
              {attachedTools?.map((item: AssignedTool) => {
                const { tool } = item;
                const itemLog = item?.log;
                const toolType = (type: boolean) =>
                  type === true ? "INTERNAL" : "EXTERNAL";
                const toolIsDeprecated = (isDeprecate: boolean) =>
                  isDeprecate === true ? "DEPRECATED" : "";

                return (
                  <Link
                    key={tool.id}
                    css={{ marginBottom: "1rem" }}
                    to={`${ROUTES.tool.index}/${tool.id}`}
                  >
                    <Tooltip
                      status="notification"
                      caption={`Assigned by: ${itemLog?.created.author}`}
                      hideTimeout={0}
                    >
                      <Badge
                        css={{ margin: "0 0 0 1rem" }}
                        color={displayToolColor(
                          tool.deprecated
                            ? toolIsDeprecated(tool.deprecated)
                            : toolType(tool.internal)
                        )}
                        text={tool.name}
                      />
                    </Tooltip>
                  </Link>
                );
              })}
            </FlexContainer>
          </FlexContainer>
          {attachedSlas.length !== 0 && (
            <Row>
              <Col>
                <Spacer height="1rem" />
                <Table small>
                  <Table.thead>
                    <Table.tr>
                      <Table.th>SLA Name</Table.th>
                      <Table.th>Description</Table.th>
                      <Table.th>Type</Table.th>
                      <Table.th>Unit Type</Table.th>
                      <Table.th>Value</Table.th>
                      <Table.th>Unit</Table.th>
                      <Table.th>Support service hours</Table.th>
                      <Table.th>Priority</Table.th>
                      <Table.th>Comment</Table.th>
                      <Table.th>Modified</Table.th>
                      {canEdit && (
                        <Table.th css={{ textAlign: "center" }}>
                          Action
                        </Table.th>
                      )}
                    </Table.tr>
                  </Table.thead>
                  <Table.tbody>
                    {attachedSlas?.map((item: AttachedSlas) => {
                      return (
                        <Table.tr key={item?.sla?.id}>
                          <Table.td>{item?.sla?.name}</Table.td>
                          <Table.td>{item?.sla?.description}</Table.td>
                          <Table.td>
                            {displaySlaType(item?.sla?.slaType)}
                          </Table.td>
                          <Table.td>
                            {displaySlaUnitType(item?.sla?.unitType)}
                          </Table.td>
                          <Table.td>{item?.effectiveValue}</Table.td>
                          <Table.td>
                            <TagWrap>
                              <Tag text={item?.sla?.range?.unit} />
                            </TagWrap>
                          </Table.td>
                          <Table.td>
                            {displayServiceHours(item?.effectiveSupportHours)}
                          </Table.td>
                          <Table.td>{item?.sla?.priority || "-"}</Table.td>
                          <Table.td>{item?.data?.comment}</Table.td>
                          <Table.td>
                            {item?.data?.log?.modified?.author} <br />
                            {item?.data?.log?.modified?.time &&
                              dayjs(item?.data.log?.modified?.time).format(
                                `${dateFormatWithHours}`
                              )}
                          </Table.td>
                          {canEdit && (
                            <Table.td hasLeftBorder>
                              <FlexContainer>
                                <EditSLA
                                  refetch={refetch}
                                  val={item?.effectiveValue}
                                  supportHours={item?.effectiveSupportHours}
                                  comm={item?.data?.comment as string}
                                  sla={item?.sla}
                                  serviceId={service?.id}
                                />
                              </FlexContainer>
                            </Table.td>
                          )}
                        </Table.tr>
                      );
                    })}
                  </Table.tbody>
                </Table>
              </Col>
            </Row>
          )}
        </div>
      )}
    </CollapseItem>
  );
}

export { ServiceItem };
